import React, { useCallback, useEffect, useId, useRef, useState } from 'react';

export interface WaitingListPayload {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
}

interface WaitingListModalProps {
  buttonText: string;
  title: string;
  buttonClassName?: string;
}

const DEFAULT_BUTTON_CLASS =
  'px-5 py-3 rounded-2xl shadow font-semibold bg-[#2f2f2d] text-[#eae6df] hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[#2f2f2d] focus:ring-offset-2';

type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * Landing-owned waiting-list sign-up modal. Collects the four fields that match
 * the backend `WaitingUser` contract and POSTs them to the existing anonymous
 * `/waitingList` endpoint. Kept separate from the shared `Modal` so the public
 * marketing page can own its brand palette and non-leaky error handling.
 */
export default function LandingWaitingListModal({
  buttonText,
  title,
  buttonClassName = DEFAULT_BUTTON_CLASS,
}: WaitingListModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');

  const triggerRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const firstFieldRef = useRef<HTMLInputElement>(null);

  const idPrefix = useId();
  const titleId = `${idPrefix}-title`;
  const firstNameId = `${idPrefix}-first-name`;
  const lastNameId = `${idPrefix}-last-name`;
  const emailId = `${idPrefix}-email`;
  const phoneNumberId = `${idPrefix}-phone`;

  const close = useCallback(() => {
    setIsOpen(false);
    setStatus('idle');
    setErrorMessage('');
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhoneNumber('');
    triggerRef.current?.focus();
  }, []);

  // Escape-to-close + focus trap while the dialog is open.
  useEffect(() => {
    if (!isOpen) return;
    const dialog = dialogRef.current;
    if (!dialog) return;

    firstFieldRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = Array.from(
        dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      ).filter((element) => !element.hasAttribute('disabled'));
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, close]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    const baseUrl = process.env.REACT_APP_API_BASE_URL ?? '';
    const payload: WaitingListPayload = { firstName, lastName, email, phoneNumber };

    try {
      const response = await fetch(`${baseUrl}/waitingList`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        // Non-2xx bodies are JSON strings, not objects (duplicate email /
        // missing email / backend failure). Surface a generic, non-leaky
        // inline error instead of parsing or echoing the body.
        setStatus('error');
        setErrorMessage(
          "We couldn't add you to the waiting list. Please double-check your details and try again."
        );
        return;
      }

      setStatus('success');
    } catch {
      setStatus('error');
      setErrorMessage("We couldn't reach the server. Please try again.");
    }
  };

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        onClick={() => setIsOpen(true)}
        className={buttonClassName}
      >
        {buttonText}
      </button>

      {isOpen && (
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        >
          <div className="w-full max-w-lg rounded-2xl bg-[#eae6df] p-6 text-[#2f2f2d] shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <h2 id={titleId} className="text-xl font-bold">
                {title}
              </h2>
              <button
                type="button"
                onClick={close}
                aria-label="Close dialog"
                className="rounded-lg px-3 py-1 text-sm hover:bg-black/10 focus:outline-none focus:ring-2 focus:ring-[#2f2f2d]"
              >
                &times;
              </button>
            </div>

            {status === 'success' ? (
              <div className="mt-4" role="status">
                <p className="text-[#2f2f2d]">
                  You&apos;re on the list! We&apos;ll be in touch soon.
                </p>
                <button
                  type="button"
                  onClick={close}
                  className="mt-4 w-full rounded-xl bg-[#2f2f2d] py-3 font-semibold text-[#eae6df] hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[#2f2f2d]"
                >
                  Done
                </button>
              </div>
            ) : (
              <form className="mt-4 space-y-4" onSubmit={handleSubmit}>
                <div>
                  <label htmlFor={firstNameId} className="block text-sm font-semibold">
                    First name
                  </label>
                  <input
                    ref={firstFieldRef}
                    id={firstNameId}
                    type="text"
                    value={firstName}
                    onChange={(event) => setFirstName(event.target.value)}
                    required
                    className="mt-1 w-full rounded-xl border border-[#2f2f2d]/30 bg-white p-3 text-[#2f2f2d] focus:outline-none focus:ring-2 focus:ring-[#2f2f2d]"
                  />
                </div>
                <div>
                  <label htmlFor={lastNameId} className="block text-sm font-semibold">
                    Last name
                  </label>
                  <input
                    id={lastNameId}
                    type="text"
                    value={lastName}
                    onChange={(event) => setLastName(event.target.value)}
                    required
                    className="mt-1 w-full rounded-xl border border-[#2f2f2d]/30 bg-white p-3 text-[#2f2f2d] focus:outline-none focus:ring-2 focus:ring-[#2f2f2d]"
                  />
                </div>
                <div>
                  <label htmlFor={emailId} className="block text-sm font-semibold">
                    Email
                  </label>
                  <input
                    id={emailId}
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    required
                    className="mt-1 w-full rounded-xl border border-[#2f2f2d]/30 bg-white p-3 text-[#2f2f2d] focus:outline-none focus:ring-2 focus:ring-[#2f2f2d]"
                  />
                </div>
                <div>
                  <label htmlFor={phoneNumberId} className="block text-sm font-semibold">
                    Phone number
                  </label>
                  <input
                    id={phoneNumberId}
                    type="tel"
                    value={phoneNumber}
                    onChange={(event) => setPhoneNumber(event.target.value)}
                    required
                    className="mt-1 w-full rounded-xl border border-[#2f2f2d]/30 bg-white p-3 text-[#2f2f2d] focus:outline-none focus:ring-2 focus:ring-[#2f2f2d]"
                  />
                </div>

                {status === 'error' && (
                  <p role="alert" className="text-sm font-semibold text-red-700">
                    {errorMessage}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full rounded-xl bg-[#2f2f2d] py-3 font-semibold text-[#eae6df] hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[#2f2f2d] disabled:opacity-60"
                >
                  {status === 'submitting' ? 'Joining…' : 'Join the waiting list'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
